import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { personalInfo, keyMetrics } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Linkedin, ExternalLink, Users, ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const About = () => {
  const { theme } = useTheme();
  const accentColor = theme.colors.accent;
  const bgColor = theme.colors.background;
  const textColor = theme.colors.text;

  return (
    <section id="about" className="py-20" style={{ backgroundColor: bgColor, color: textColor }}>
      <div className="container mx-auto px-6">
        <div className="mb-16 text-center">
          <h2 className="mb-4 font-serif text-4xl font-bold md:text-5xl">About Me</h2>
          <div className="mx-auto h-1 w-24 rounded" style={{ backgroundColor: accentColor }}></div>
        </div>

        <div className="mb-16 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="flex flex-col items-center gap-6">
            <div className="relative">
              <img
                src={personalInfo.image}
                alt={personalInfo.name}
                className="h-80 w-80 rounded-full border-4 object-cover shadow-2xl"
                style={{ borderColor: accentColor }}
              />
            </div>
            
            {/* Followers Badge */}
            <div
              className="flex items-center gap-2 rounded-full border-2 px-6 py-3 font-medium shadow-lg"
              style={{ borderColor: accentColor, backgroundColor: 'white' }}
            >
              <Users className="h-5 w-5" style={{ color: accentColor }} />
              <span className="font-semibold" style={{ color: textColor }}>
                {personalInfo.followers} Followers
              </span>
            </div>
          </div>

          <div>
            <div className="mb-6 flex items-center gap-4">
              <img src={personalInfo.logo} alt="VJS Logo" className="h-16 w-16 object-contain" />
              <h3 className="font-serif text-3xl font-bold">{personalInfo.fullName}</h3>
            </div>
            <p className="mb-4 text-xl" style={{ color: accentColor }}>
              {personalInfo.title}
            </p>
            <p className="mb-6 text-lg leading-relaxed opacity-90">{personalInfo.bio}</p>

            <div className="mb-6 flex flex-wrap gap-4">
              <RouterLink
                to="/biography"
                data-testid="read-biography-link"
                className="inline-flex items-center gap-2 font-semibold transition-all hover:gap-3"
                style={{ color: accentColor }}
              >
                Read full biography <ArrowRight className="h-4 w-4" />
              </RouterLink>
              <RouterLink
                to="/recruiteriq"
                data-testid="explore-recruiteriq-link"
                className="inline-flex items-center gap-2 font-semibold transition-all hover:gap-3"
                style={{ color: accentColor }}
              >
                Explore RecruiterIQ <ArrowRight className="h-4 w-4" />
              </RouterLink>
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href={personalInfo.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-all hover:scale-105 hover:shadow-lg"
                style={{ backgroundColor: accentColor }}
              >
                <Linkedin className="h-5 w-5" />
                LinkedIn Profile
              </a>
              <a
                href={personalInfo.socialLinks.wiki}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-all hover:scale-105 hover:shadow-lg"
                style={{ backgroundColor: accentColor }}
              >
                <ExternalLink className="h-5 w-5" />
                Wiki Page
              </a>
              <a
                href={personalInfo.socialLinks.recruiterIQ}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-all hover:scale-105 hover:shadow-lg"
                style={{ backgroundColor: accentColor }}
              >
                <ExternalLink className="h-5 w-5" />
                Recruiter IQ
              </a>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {keyMetrics.map((metric) => (
            <Card
              key={metric.id}
              className="border-none text-center shadow-lg transition-all hover:scale-105"
              style={{ backgroundColor: '#ffffff' }}
            >
              <CardContent className="p-6">
                <div className="mb-2 font-serif text-4xl font-bold" style={{ color: accentColor }}>
                  {metric.value}
                </div>
                <div className="mb-1 text-lg font-semibold" style={{ color: textColor }}>
                  {metric.label}
                </div>
                <div className="text-sm opacity-70">{metric.description}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
